import { FC } from 'react'
import { ExternalUser } from '../../../../../generated'
import { AdversaryUserList } from '../basicDetails/AdversaryUserList'
import { ClientUserList } from '../basicDetails/ClientUserList'

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'

// others
import { Controller, useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { ExternalUserRoleRole, useFetchAllExternalUsersLazyQuery } from '../../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../../constants'
import { titleize } from '../../../../utils'
import { formatAddress } from '../../../../utils/legalTeam'
import { AddNewClient } from '../basicDetails/AddNewClient'
import { AddNewAdversary } from '../basicDetails/AddNewAdversary'
import { ExternalUsersResult } from '../../../../interfaceTypes/common'

type CasePartiesProps = {
  isConsultMatter?: boolean
  clientListRef: React.Ref<{ appendClientUser: (user: ExternalUser) => void }>
  adversaryListRef: React.Ref<{ appendAdversaryUser: (user: ExternalUser) => void }>
  contactListRef?: React.Ref<{ appendContactUser: (user: ExternalUser) => void }>
}

const CaseParties: FC<CasePartiesProps> = ({ isConsultMatter, clientListRef, adversaryListRef, contactListRef }) => {
  return (
    <>
      <PrimaryClient role={ExternalUserRoleRole.Client} name="client" isConsultMatter={isConsultMatter} />
      <Box m={4} />
      <ClientUserList ref={clientListRef} />
      <PrimaryClient role={ExternalUserRoleRole.Adversary} name="adversary" />
      <Box m={4} />
      <AdversaryUserList ref={adversaryListRef} />
    </>
  )
}

export const PrimaryClient = ({ role, name, isConsultMatter }: { role: ExternalUserRoleRole; name: string, isConsultMatter?: boolean }) => {
  const [externalUsersData, setExternalUsersData] = useState<ExternalUsersResult>([])
  const [newExternalUser, setNewExternalUser] = useState<ExternalUser>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchTerm, setSearchTerm] = useState<string>('')

  const [openModal, setOpenModal] = useState('')

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const {
    control,
    formState: { errors },
    trigger,
    setValue
  } = useFormContext()

  const handleOpen = (type: string) => {
    setOpenModal(type)
  }

  const customClose = () => setOpenModal('')

  const [fetchAllExternalUsers, { loading }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const { fetchAllExternalUsers } = data

        if (fetchAllExternalUsers) {
          const { externalUsers, response, pagination } = fetchAllExternalUsers
          const { page } = pagination || {}
          const { status } = response || {}

          if (status === 200 && externalUsers) {
            externalUsersData && page !== 1
              ? setExternalUsersData([...externalUsersData, ...externalUsers])
              : setExternalUsersData(externalUsers)
          }
        }
      }
    }
  })

  const getOptions = () =>
    externalUsersData?.map(user => ({
      id: user?.id,
      firstName: user?.firstName,
      lastName: user?.lastName,
      displayName: user?.displayName,
      email: user?.email,
      clientNumber: user?.clientNumber,
      workPhone: user?.phoneNumber,
      defaultBillingAddress: formatAddress(user?.addresses)
    }))

  useEffect(() => {
    fetchAllExternalUsers({
      variables: {
        externalUsersInput: {
          roles: [role],

          paginationOptions: {
            limit: 20,
            page: currentPage
          },
          searchValue: debouncedSearchTerm
        }
      }
    })
    // eslint-disable-next-line
  }, [currentPage, debouncedSearchTerm])

  useEffect(() => {
    if (debouncedSearchTerm) {
      setCurrentPage(1)
    }
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (newExternalUser?.id) {
      setValue(name, {
        id: newExternalUser.id,
        email: newExternalUser.email || '',
        firstName: newExternalUser.firstName,
        lastName: newExternalUser.lastName,
        clientNumber: newExternalUser.clientNumber || '',
        workPhone: newExternalUser.phoneNumber || '',
        caseRole: role,
        defaultBillingAddress: formatAddress(newExternalUser.addresses)
      })
    }
  }, [newExternalUser])

  const label = `Primary ${titleize(name)}`

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Typography variant="h3" fontSize={14} fontWeight={700} color={'primary.main'}>
        {label}
      </Typography>
      <FormControl variant="outlined" fullWidth>
        <Grid container>
          <Grid container item spacing={2.5} alignItems="center" sx={{ position: 'relative' }}>
            <Grid item xs={12} md={8}>
              <Controller
                name={name}
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <Autocomplete
                      fullWidth
                      options={getOptions() || []}
                      value={value || null}
                      filterOptions={createFilterOptions({ ignoreCase: true })}
                      loading={loading}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      autoHighlight={searchTerm.trim().length > 0}
                      autoSelect={searchTerm.trim().length > 0}
                      clearOnEscape
                      onChange={(event, item, reason) => {
                        if (reason === 'clear' || !item) {
                          onChange(null)
                          trigger(name)
                          return
                        }
                        onChange({
                          ...value,
                          ...item
                        })
                        trigger(name)
                      }}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'clear' || reason === 'reset') {
                          setSearchTerm('')
                          return
                        }
                        setSearchTerm(newInputValue)
                      }}
                      ListboxProps={{
                        onScroll: event => {
                          const listboxNode = event.currentTarget
                          if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                            setCurrentPage(currentPage + 1)
                          }
                        },
                        role: 'list-box',
                        style: {
                          maxHeight: '190px'
                        }
                      }}
                      getOptionLabel={item => {
                        const { displayName, email, clientNumber, id, firstName, lastName } = item || {}
                        const name = displayName || `${firstName || ''} ${lastName || ''}`.trim()
                        const options = `${name} ${clientNumber ? `- #${clientNumber}` : ''}  ${email ? `(${email})` : ''}`
                        return id ? options : ''
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={titleize(name)}
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" fullWidth>
                <Controller
                  name={`${name}.caseRole`}
                  control={control}
                  render={({ field, fieldState: { error: { message } = {} } }) => {
                    return (
                      <TextField {...field} label="Matter Role" error={!!message} value={label} defaultValue={label} />
                    )
                  }}
                />
              </FormControl>
            </Grid>  
          </Grid>

          <Grid item md={8} xs={12} display={'flex'} alignItems={'center'} justifyContent={'space-between'} mt={0.5}>
            <FormHelperText error={!!(errors[name] as any)?.message || !!(errors[name] as any)?.id?.message}>
              {typeof (errors[name] as any)?.message === 'string' ||
              typeof (errors[name] as any)?.id?.message === 'string'
                ? (errors[name] as any).message || (errors[name] as any)?.id.message
                : ' '}
            </FormHelperText>

            <Button size="small" variant="text" onClick={() => handleOpen(name)}>
              <span style={{ fontSize: 11 }}>{titleize(`Create New Primary ${name}`)}</span>
            </Button>
          </Grid>
        </Grid>
      </FormControl>

      <AddNewClient
        isConsultMatter={isConsultMatter}
        customOpen={openModal === 'client'}
        handleCustomClose={customClose}
        setNewExternalUser={setNewExternalUser}
      />
      <AddNewAdversary
        customOpen={openModal === 'adversary'}
        handleCustomClose={customClose}
        setNewExternalUser={setNewExternalUser}
      />
    </Box>
  )
}

export default CaseParties
